import { logger } from '@vagabondinternet/logging';
import { REGISTER_MESSAGES } from '@words-happen/screens';
import type { SocialProvider } from '@words-happen/ui-components';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  authErrorCode,
  authFailure,
  browserIdentitySession,
  browserResetProviders,
  type IdentitySession,
  identityPlatformPasswordReset,
  signInWithEmail as identityPlatformSignIn,
  type PasswordReset,
  passwordResetLinkFailure,
  type ResetProviders,
  ResetProvidersError,
  resetEligibility,
  type SignInWithEmail,
} from '../../api';
import { type BrowserNavigation, useAuthNavigation } from '../../navigation';
import { ROUTES } from '../../routes';
import { HandoffScreen, useSessionHandoff } from '../../session';
import { ACTION_CODE_PARAM, ACTION_MODE_PARAM } from '../verify-email-confirm/verify-email-confirm-page';
import { RESET_PASSWORD_MESSAGES, resetErrorMessage } from './reset-password-messages';
import { ResetPasswordScreen, type ResetPasswordState } from './reset-password-screen';

export const RESET_PASSWORD_MODE = 'resetPassword';

/** The oobCode of a reset-password link, or undefined when it is missing or the link is for another action. */
export const resetCode = (params: URLSearchParams): string | undefined => {
  const mode = params.get(ACTION_MODE_PARAM);
  const code = params.get(ACTION_CODE_PARAM);
  return code && (mode === null || mode === RESET_PASSWORD_MODE) ? code : undefined;
};

export interface ResetPasswordPageProps {
  /** Identity Platform's password reset. Defaults to the browser's, loading Firebase on first use. */
  passwordReset?: PasswordReset;
  /**
   * Which providers the link's account signs in with (POST /auth/reset-providers on words-happen-gql). Defaults to the
   * browser's.
   */
  resetProviders?: ResetProviders;
  /** Signs in with the new password. Defaults to Identity Platform's. */
  signInWithEmail?: SignInWithEmail;
  /** Firebase's signed-in user and the API's session endpoints. Defaults to the browser's. */
  session?: IdentitySession;
  /** How a signed-in player leaves the app. Defaults to the window's history and location. */
  navigation?: BrowserNavigation;
}

/**
 * /reset-password?oobCode=…: checks a reset link (verifyPasswordResetCode, which never uses it up) and shows the
 * account's address with a new password form. Submitting sets the password (confirmPasswordReset), signs in with it
 * and continues as /sign-in does: a verified player is handed off to return_to or GAME_HOME_URL, an unverified one goes
 * to /verify-email. If that sign-in fails, the player is signed out of Firebase and goes to /sign-in, prefilled,
 * with "Your password has been changed".
 *
 * Before the form, the page asks words-happen-gql which providers the account signs in with. An account with a
 * social provider and no password is told to use the provider, with no form, so a reset can't add a password to it;
 * one with a password, or an email-link-only one, resets as normal. If that check fails for any reason, the page says
 * something went wrong and offers to try again: it never falls back to the form.
 */
export const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({
  passwordReset = identityPlatformPasswordReset,
  resetProviders = browserResetProviders,
  signInWithEmail = identityPlatformSignIn,
  session = browserIdentitySession,
  navigation,
}) => {
  const { switchTo, leave, returnTo } = useAuthNavigation(navigation);
  const [params] = useSearchParams();
  const [code] = useState(() => resetCode(params));
  const [state, setState] = useState<ResetPasswordState>('checking');
  const [email, setEmail] = useState<string>();
  const [socialProviders, setSocialProviders] = useState<SocialProvider[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  // React StrictMode mounts twice in development; the link is checked once per attempt.
  const started = useRef(false);

  const toVerifyEmail = useCallback(
    () => switchTo(ROUTES.verifyEmail, email ? { email } : undefined, { replace: true }),
    [switchTo, email],
  );
  const handoff = useSessionHandoff({ session, returnTo, leave, onUnverified: toVerifyEmail });

  /** To /sign-in after the password changed: prefilled, saying so, and with no Firebase user to sign in silently. */
  const toSignInChanged = async () => {
    try {
      await session.signOut();
    } catch (cause) {
      logger.warn({ message: 'Signing out of Firebase failed', code: authErrorCode(cause) });
    }
    switchTo(ROUTES.signIn, { ...(email ? { email } : {}), passwordChanged: true }, { replace: true });
  };

  const check = useCallback(async () => {
    setState('checking');
    setError(undefined);
    if (!code) {
      setState('invalid');
      return;
    }
    try {
      setEmail(await passwordReset.checkResetCode(code));
    } catch (cause) {
      logger.error({ message: 'Checking the password reset link failed', code: authErrorCode(cause) });
      const failure = passwordResetLinkFailure(cause);
      if (!failure) {
        setError(resetErrorMessage(cause, RESET_PASSWORD_MESSAGES.checkFailed));
      }
      setState(failure ?? 'failed');
      return;
    }
    try {
      const eligibility = resetEligibility(await resetProviders(code));
      if (eligibility.kind === 'social-only') {
        setSocialProviders(eligibility.providers);
        setState('social-only');
        return;
      }
      setState('form');
    } catch (cause) {
      logger.error({
        message: "Checking the reset link's providers failed",
        status: cause instanceof ResetProvidersError ? cause.status : 'unknown',
      });
      setError(RESET_PASSWORD_MESSAGES.providersCheckFailed);
      setState('failed');
    }
  }, [code, passwordReset, resetProviders]);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    void check();
  }, [check]);

  /** Signs in with the new password and carries on as /sign-in does. */
  const signIn = async (address: string, password: string) => {
    try {
      const { verified } = await signInWithEmail({ email: address, password });
      if (verified) {
        await handoff.complete();
      } else {
        switchTo(ROUTES.verifyEmail, { email: address }, { replace: true });
      }
    } catch (cause) {
      logger.error({ message: 'Signing in after a password reset failed', code: authErrorCode(cause) });
      await toSignInChanged();
    }
  };

  /** Shows why setting the password failed: on the field, as a link that can't be used, or as an alert. */
  const failReset = (cause: unknown) => {
    logger.error({ message: 'Setting the new password failed', code: authErrorCode(cause) });
    if (authFailure(cause) === 'password-policy') {
      setPasswordError(REGISTER_MESSAGES.passwordPolicy);
      return;
    }
    const failure = passwordResetLinkFailure(cause);
    if (failure) {
      setState(failure);
      return;
    }
    setError(resetErrorMessage(cause, RESET_PASSWORD_MESSAGES.resetFailed));
  };

  const submit = async (password: string) => {
    if (!code || !email) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    setPasswordError(undefined);
    try {
      await passwordReset.confirmReset(code, password);
    } catch (cause) {
      failReset(cause);
      setSubmitting(false);
      return;
    }
    await signIn(email, password);
    setSubmitting(false);
  };

  if (handoff.state.phase !== 'idle') {
    return (
      <HandoffScreen
        handoff={{
          ...handoff,
          // The link is used up: "Sign in again" and "Back to sign in" sign out and continue on /sign-in.
          startOver: toSignInChanged,
        }}
      />
    );
  }

  return (
    <ResetPasswordScreen
      state={state}
      email={email}
      socialProviders={socialProviders}
      submitting={submitting}
      error={error}
      passwordError={passwordError}
      onSubmit={(password) => void submit(password)}
      onRetry={() => void check()}
      onRequestNewLink={() => switchTo(ROUTES.forgotPassword, email ? { email } : undefined)}
      onSignIn={() => switchTo(ROUTES.signIn, email ? { email } : undefined)}
    />
  );
};
